"use client";

import { cn } from "@/lib/utils/index";
import * as Toast from "@radix-ui/react-toast";
import { AlertTriangle, Check, X } from "lucide-react";

/** Toast severities. `success` and `error` are the standard CRUD-feedback
 *  channels; `warning` (amber) is for non-error prompts such as
 *  click-again-to-confirm hints on destructive actions where a red
 *  "Error" toast would misrepresent intent. */
export type ToastSeverity = "success" | "error" | "warning";

/** Per-severity badge styling: a colored circle with a white glyph. */
const SEVERITY_ICON: Record<ToastSeverity, { bg: string; Icon: typeof Check }> = {
  success: { bg: "bg-green-600", Icon: Check },
  warning: { bg: "bg-amber-500", Icon: AlertTriangle },
  error: { bg: "bg-red-600", Icon: X },
};

/**
 * Renders the colored severity badge shown to the left of a toast's text.
 *
 * @param type - The toast severity that determines the badge color and glyph.
 * @returns The badge element.
 */
export const renderSeverityIcon = (type: ToastSeverity) => {
  const { bg, Icon } = SEVERITY_ICON[type];
  return (
    <div className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded-full", bg)}>
      <Icon className="h-3 w-3 text-white" />
    </div>
  );
};

interface FetchToastProps {
  message: string;
  type?: ToastSeverity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Nowadays themed, controlled toast component using Radix UI.
 * For most use cases, use the `FetchToastProvider` and `useToast` hook instead.
 *
 * @param props - Component props.
 * @param props.message - The toast message to display.
 * @param props.type - Severity controlling the badge icon. Defaults to `"success"`.
 * @param props.open - Whether the toast is currently shown (controlled).
 * @param props.onOpenChange - Called when the open state changes (e.g., dismissal).
 * @returns The controlled toast element.
 */
export const FetchToast = ({ message, type = "success", open, onOpenChange }: FetchToastProps) => {
  return (
    <Toast.Provider swipeDirection="left">
      <Toast.Root
        className={cn(
          "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-[transform,opacity]",
          "data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-(--radix-toast-swipe-end-x) data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x) data-[swipe=move]:transition-none",
          "data-[state=open]:animate-slideUpAndFade data-[state=closed]:animate-slideDownAndFadeOut data-[swipe=end]:animate-slideDownAndFadeOut",
          "border-gray-200 bg-white text-gray-900"
        )}
        open={open}
        onOpenChange={onOpenChange}
      >
        <div className="flex items-center space-x-2">
          {renderSeverityIcon(type)}
          <Toast.Description className="text-sm font-medium">{message}</Toast.Description>
        </div>
        <Toast.Close
          className="absolute right-2 top-2 rounded-md p-1 text-gray-500 opacity-0 transition-opacity hover:text-gray-900 focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" />
        </Toast.Close>
      </Toast.Root>
      <Toast.Viewport className="fixed top-0 z-100 flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:left-0 sm:top-auto sm:flex-col md:max-w-[420px]" />
    </Toast.Provider>
  );
};
